import type {
    NaturalComparisonObservation,
    NaturalComparisonRelation,
    NaturalComparisonSlotObservation,
    NaturalLessThanTrace,
} from '@/adapters/primitive/_contract/natural/comparison';

function assertNatural(value: number, label: string): void {
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new RangeError(`${label} must be a non-negative safe integer.`);
    }
}

function assertRadix(radix: number): void {
    if (!Number.isSafeInteger(radix) || radix < 2) {
        throw new RangeError('Natural comparison radix must be an integer greater than one.');
    }
}

function powersForNatural(value: number, radix: number): number[] {
    if (value === 0) return [0];
    const powers: number[] = [];
    for (let remaining = value; remaining > 0; remaining = Math.floor(remaining / radix)) {
        powers.push(powers.length);
    }
    return powers;
}

function digitAtPower(value: number, power: number, radix: number): number {
    return Math.floor(value / radix ** power) % radix;
}

function compareDigits(sourceDigit: number, targetDigit: number): NaturalComparisonRelation {
    if (sourceDigit < targetDigit) return 'less';
    if (sourceDigit > targetDigit) return 'greater';
    return 'equal';
}

function compareSlots(args: {
    sourceDigit: number;
    targetDigit: number;
    radix: number;
}): NaturalComparisonSlotObservation[] {
    const observations: NaturalComparisonSlotObservation[] = [];
    for (let slot = 1; slot < args.radix; slot += 1) {
        const sourceCounted = slot <= args.sourceDigit;
        const targetCounted = slot <= args.targetDigit;
        const outcome = sourceCounted !== targetCounted
            ? sourceCounted ? 'greater' : 'less'
            : !sourceCounted || slot === args.radix - 1
                ? 'equal'
                : 'continue';
        observations.push({ slot, sourceCounted, targetCounted, outcome });
        if (outcome !== 'continue') break;
    }
    return observations;
}

export function deriveNaturalLessThanTrace(args: {
    source: number;
    target: number;
    radix: number;
}): NaturalLessThanTrace {
    assertNatural(args.source, 'NaturalLessThan source');
    assertNatural(args.target, 'NaturalLessThan target');
    assertRadix(args.radix);

    const maxPower = Math.max(
        powersForNatural(args.source, args.radix).at(-1) ?? 0,
        powersForNatural(args.target, args.radix).at(-1) ?? 0,
    );
    const powers = Array.from({ length: maxPower + 1 }, (_, index) => maxPower - index);
    const observations: NaturalComparisonObservation[] = [];

    for (const power of powers) {
        const sourceDigit = digitAtPower(args.source, power, args.radix);
        const targetDigit = digitAtPower(args.target, power, args.radix);
        const relation = compareDigits(sourceDigit, targetDigit);
        const slots = compareSlots({ sourceDigit, targetDigit, radix: args.radix });
        observations.push({ power, sourceDigit, targetDigit, relation, slots });
        if (relation !== 'equal') {
            return {
                ...args,
                powers,
                observations,
                decision: relation === 'less',
                decisivePower: power,
            };
        }
    }

    return {
        ...args,
        powers,
        observations,
        decision: false,
        decisivePower: null,
    };
}
